"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import type { FeaturedPrice } from "@/lib/api";
import { hasPriceStrip, isPopupDismissed, isSitewideTopbar, popupDomId, sanitizeColor, type PublicPopup } from "@/lib/popups";
import PopupCloseButton from "./PopupCloseButton";
import PriceStrip from "./PriceStrip";
import TopbarShell from "./TopbarShell";

type Props = {
  locale: string;
};

const API_BASE = (() => {
  const override = process.env.NEXT_PUBLIC_API_URL?.trim() ?? "";
  if (typeof window !== "undefined") {
    const bakedLocalhost = /^https?:\/\/(localhost|127\.0\.0\.1)/i.test(override);
    return (override && !bakedLocalhost ? override : window.location.origin).replace(/\/$/, "");
  }
  return (override || "http://localhost:8096").replace(/\/$/, "");
})();

function stripLocalePrefix(pathname: string, locale: string): string {
  const normalized = pathname || "/";
  const prefix = `/${locale}`;
  if (normalized === prefix) return "/";
  if (normalized.startsWith(`${prefix}/`)) return normalized.slice(prefix.length) || "/";
  return normalized;
}

/**
 * sanitize-html TEMBEL YUKLENIR.
 *
 * Bu dosya "use client" oldugu icin statik import, sanitize-html +
 * htmlparser2 + postcss'i HER sayfanin ilk paketine sokuyordu: 49 KB'i
 * calistirilmayan kod (16 Eyl 2026 Lighthouse, unused-javascript'in en buyuk
 * kendi-kodumuz kalemi). Popup'li sayfa nadir; modul yalnizca gercekten HTML
 * icerikli bir popup gosterilecekse indirilir.
 *
 * Guvenlik ozelligi korunur: modul yuklenene kadar icerik BOS kalir,
 * temizlenmemis HTML hicbir anda basilmaz.
 */
function useSanitizedHtml(raw: string | null | undefined): string {
  const [clean, setClean] = useState("");
  useEffect(() => {
    if (!raw) { setClean(""); return; }
    let active = true;
    void import("@/lib/sanitize-html").then(({ sanitizeCmsHtml }) => {
      if (active) setClean(sanitizeCmsHtml(raw));
    });
    return () => { active = false; };
  }, [raw]);
  return clean;
}

function PopupBody({ popup }: { popup: PublicPopup }) {
  const content = useSanitizedHtml(popup.content);
  const buttonStyle = {
    backgroundColor: sanitizeColor(popup.button_color),
    color: sanitizeColor(popup.button_text_color),
  };

  const body = (
    <>
      {popup.image ? (
        <div className="relative h-28 w-full overflow-hidden rounded-md bg-(--color-bg-alt)">
          <Image
            src={popup.image}
            alt={popup.alt || popup.title}
            fill
            sizes="(max-width: 640px) calc(100vw - 2rem), 32rem"
            className="object-cover"
            unoptimized
          />
        </div>
      ) : null}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold leading-snug">{popup.title}</p>
        {content ? (
          <div
            className="mt-1 text-sm leading-relaxed opacity-85 [&_a]:underline"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        ) : null}
      </div>
      {popup.button_text && popup.link_url ? (
        <span
          className="inline-flex h-9 shrink-0 items-center justify-center rounded-md bg-(--color-brand) px-3 text-sm font-semibold text-(--color-brand-fg)"
          style={buttonStyle}
        >
          {popup.button_text}
        </span>
      ) : null}
    </>
  );

  if (!popup.link_url) return <div className="contents">{body}</div>;

  const external = popup.link_target === "_blank";
  return (
    <Link
      href={popup.link_url}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      className="contents"
    >
      {body}
    </Link>
  );
}

/** Yol hedefli fiyat şeridi (site geneli olan sunucuda basılır). */
function ClientPriceStrip({ popup }: { popup: PublicPopup }) {
  const [items, setItems] = useState<FeaturedPrice[]>([]);
  useEffect(() => {
    const ctrl = new AbortController();
    fetch(`${API_BASE}/api/v1/prices/featured-list?limit=12`, { signal: ctrl.signal, headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : { items: [] }))
      .then((data: { items?: FeaturedPrice[] }) => setItems(data.items ?? []))
      .catch(() => undefined);
    return () => ctrl.abort();
  }, []);
  return <PriceStrip items={items} marquee={popup.text_behavior === "marquee"} speed={popup.scroll_speed} />;
}

function TopbarPopup({ popup }: { popup: PublicPopup }) {
  return (
    <TopbarShell popup={popup}>
      {hasPriceStrip(popup) ? (
        <ClientPriceStrip popup={popup} />
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-3 truncate">
          <PopupBody popup={popup} />
        </div>
      )}
    </TopbarShell>
  );
}

function SidebarPopup({ popup }: { popup: PublicPopup }) {
  const verticalClass =
    popup.type === "sidebar_top"
      ? "md:top-32"
      : popup.type === "sidebar_center"
        ? "md:top-1/2 md:-translate-y-1/2"
        : "md:bottom-8";

  return (
    <aside
      id={popupDomId(popup.id)}
      className={`fixed bottom-[calc(4.75rem+env(safe-area-inset-bottom))] right-3 z-40 w-[min(22rem,calc(100vw-1.5rem))] rounded-lg border border-(--color-border-soft) bg-(--color-surface) p-3 text-(--color-foreground) shadow-2xl md:bottom-auto md:right-5 ${verticalClass}`}
      style={{
        backgroundColor: sanitizeColor(popup.background_color) ?? undefined,
        color: sanitizeColor(popup.text_color) ?? undefined,
      }}
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1 space-y-2">
          <PopupBody popup={popup} />
        </div>
        <PopupCloseButton popup={popup} />
      </div>
    </aside>
  );
}

export default function PublicPopups({ locale }: Props) {
  const pathname = usePathname();
  const currentPath = useMemo(() => stripLocalePrefix(pathname || "/", locale), [locale, pathname]);
  const [items, setItems] = useState<PublicPopup[]>([]);

  useEffect(() => {
    const ctrl = new AbortController();
    const params = new URLSearchParams({
      locale,
      default_locale: "tr",
      current_path: currentPath,
      limit: "20",
      sort: "display_order",
      order: "asc",
    });

    fetch(`${API_BASE}/api/v1/popups?${params.toString()}`, {
      signal: ctrl.signal,
      credentials: "include",
      headers: { Accept: "application/json" },
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (!Array.isArray(data)) return;
        // Site geneli üst şerit SitewideTopbar'da sunucuda basılır; burada ikinci kez çizilmez.
        setItems(data.filter((item: PublicPopup) => !isPopupDismissed(item) && !isSitewideTopbar(item)));
      })
      .catch((err) => {
        if (err?.name !== "AbortError") setItems([]);
      });

    return () => ctrl.abort();
  }, [currentPath, locale]);

  const topbar = items.find((item) => item.type === "topbar");
  const sidebar = items.filter((item) => item.type !== "topbar").slice(0, 3);

  return (
    <>
      {topbar ? <TopbarPopup popup={topbar} /> : null}
      {sidebar.map((popup) => (
        <SidebarPopup key={popup.id} popup={popup} />
      ))}
    </>
  );
}
